import { DEFAULT_SCORING, DEFAULT_SETTINGS } from '../data/scoring.js';
import { withCache } from './almanacCache.js';

// Yahoo stat_id → Mock1 scoring key (or handler)
// Values marked 'yds' need pts-per-yard → ydsPer conversion
const STAT_MAP = {
  4:  { key: 'passYdsPer',       type: 'yds' },
  5:  { key: 'passTD',           type: 'pts' },
  6:  { key: 'passInt',          type: 'pts' },
  9:  { key: 'rushYdsPer',       type: 'yds' },
  10: { key: 'rushTD',           type: 'pts' },
  11: { key: 'receptions',       type: 'pts' },
  12: { key: 'recYdsPer',        type: 'yds' },
  13: { key: 'recTD',            type: 'pts' },
  15: { key: 'returnTD',         type: 'pts' },
  17: { key: 'fumbles',          type: 'pts' },
  18: { key: 'fumblesLost',      type: 'pts' },
  // Kicking
  19: { key: 'fgMade0_19',       type: 'pts' },
  20: { key: 'fgMade20_29',      type: 'pts' },
  21: { key: 'fgMade30_39',      type: 'pts' },
  22: { key: 'fgMade40_49',      type: 'pts' },
  23: { key: 'fgMade50',         type: 'pts' },
  29: { key: 'patMade',          type: 'pts' },
  30: { key: 'patMissed',        type: 'pts' },
  // Defense
  32: { key: 'defSack',          type: 'pts' },
  33: { key: 'defInt',           type: 'pts' },
  34: { key: 'defFumRec',        type: 'pts' },
  35: { key: 'defTD',            type: 'pts' },
  36: { key: 'defSafety',        type: 'pts' },
  37: { key: 'defBlockKick',     type: 'pts' },
  50: { key: 'defPtsAllowed0',   type: 'pts' },
  51: { key: 'defPtsAllowed1_6', type: 'pts' },
  52: { key: 'defPtsAllowed7_13',  type: 'pts' },
  53: { key: 'defPtsAllowed14_20', type: 'pts' },
  54: { key: 'defPtsAllowed21_27', type: 'pts' },
  55: { key: 'defPtsAllowed28_34', type: 'pts' },
  56: { key: 'defPtsAllowed35',  type: 'pts' },
};

// Yahoo position label → Mock1 position key
const POSITION_MAP = {
  QB:      'QB',
  RB:      'RB',
  WR:      'WR',
  TE:      'TE',
  'W/R/T': 'FLEX',
  'Q/W/R/T': 'SFLEX',
  DEF:     'DEF',
  K:       'K',
  BN:      'BN',
};

export function parseYahooSettings(rawLeague) {
  const scoring = { ...DEFAULT_SCORING };

  // PPR defaults to 0 unless stat_id 11 is explicitly present in stat_modifiers
  scoring.receptions = 0;

  const statMods = rawLeague.stat_modifiers ?? [];

  // Map stat_modifiers to Mock1 scoring keys
  // Data arrives already unwrapped: [{stat_id, value, bonuses?}, ...]
  // stat_id may be int or string; value may be float or string
  for (const mod of statMods) {
    const id = Number(mod.stat_id);
    const value = parseFloat(mod.value);
    const mapping = STAT_MAP[id];
    if (!mapping || isNaN(value)) continue;

    if (mapping.type === 'yds') {
      // Yahoo stores pts-per-yard (e.g. 0.05 = 1pt per 20 yds)
      // Mock1 stores ydsPer (e.g. 20 means 1pt per 20 yds)
      scoring[mapping.key] = value !== 0 ? Math.round(1 / value) : DEFAULT_SCORING[mapping.key];
    } else {
      scoring[mapping.key] = value;
    }
  }

  // Parse yardage bonuses from stat_modifiers into scoring object
  // Shape: {stat_id, value, bonuses: [{bonus: {target, points}}]}
  const BONUS_STAT_MAP = {
    4:  { ptsKey: 'passYdsBonus', threshKey: 'passYdsBonusThreshold' },
    9:  { ptsKey: 'rushYdsBonus', threshKey: 'rushYdsBonusThreshold' },
    12: { ptsKey: 'recYdsBonus',  threshKey: 'recYdsBonusThreshold' },
  };
  for (const mod of statMods) {
    const id = Number(mod.stat_id);
    const mapping = BONUS_STAT_MAP[id];
    if (!mapping || !Array.isArray(mod.bonuses) || mod.bonuses.length === 0) continue;
    const b = mod.bonuses[0]?.bonus ?? mod.bonuses[0];
    const target = parseInt(b.target, 10);
    const points = parseFloat(b.points);
    if (!isNaN(target) && !isNaN(points)) {
      scoring[mapping.ptsKey] = points;
      scoring[mapping.threshKey] = target;
    }
  }

  // Map roster_positions to Mock1 positions object
  // Data arrives already unwrapped: [{position, count, is_starting_position}, ...]
  const positions = { QB: 0, RB: 0, WR: 0, TE: 0, FLEX: 0, SFLEX: 0, DEF: 0, K: 0, BN: 0 };
  for (const slot of rawLeague.roster_positions ?? []) {
    const mock1Key = POSITION_MAP[slot.position];
    if (mock1Key !== undefined) {
      positions[mock1Key] = (positions[mock1Key] ?? 0) + parseInt(slot.count, 10);
    }
  }

  const isAuction = rawLeague.is_auction_draft === '1'
    || rawLeague.is_auction_draft === 1
    || rawLeague.is_auction_draft === true;

  return {
    name: rawLeague.name,
    season: rawLeague.season,
    league_key: rawLeague.league_key,
    budget: DEFAULT_SETTINGS.budget,   // Yahoo doesn't expose auction budget via this endpoint
    teams: parseInt(rawLeague.num_teams, 10) || DEFAULT_SETTINGS.teams,
    is_auction_draft: isAuction,
    scoring_type: rawLeague.scoring_type,
    scoring,
    positions,
  };
}

// ============================================================================
// RECAP — multi-season history fetcher
// ============================================================================
//
// Yahoo analog of fetchSleeperLeagueHistory. The caller (the recap discovery
// UI) has already chosen which seasons to pull, so this function just
// iterates them and assembles the per-season payloads that yahooToMatchups
// + yahooToChampionships expect.
//
// All Yahoo calls go through this app's own serverless endpoints
// (/api/yahoo/teams, /standings, /scoreboard) which proxy the request with
// the user's token — keeps the SAME 1-req-per-Yahoo-call relationship the
// rate limiter cares about. Throttling is browser-side here because each
// fetch() to a serverless endpoint maps to exactly one Yahoo call.
//
// Signature mirrors fetchSleeperLeagueHistory: { seasons, error } with
// seasons in the same order the caller supplied (typically newest-first).

const DEFAULT_THROTTLE_MS = 1100; // 1 req/sec + buffer; Yahoo enforces ~1/sec sustained
const DEFAULT_MAX_WEEKS = 22;

export async function fetchYahooLeagueHistory(leagueKeys, token, options = {}) {
  if (!Array.isArray(leagueKeys) || !leagueKeys.length) {
    return { seasons: null, error: 'No league_keys supplied' };
  }
  if (!token) {
    return { seasons: null, error: 'OAuth token is required' };
  }

  const throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
  const maxWeeks = options.maxWeeks ?? DEFAULT_MAX_WEEKS;
  const onProgress = options.onProgress || (() => {});

  // True tick count per season (verified by counting tick()/recordResult()
  // calls in the loop below): teams, standings, settings, scoreboards
  // (one tick per season — the inner week loop renders progress messages
  // but does NOT tick), draft, trades, keepers. = 7 ticks. The old estimate
  // of 19 assumed 17 per-week scoreboard ticks and made the bar under-report
  // by ~4× (Smithtown showed 23% at 18/20 seasons). Keepers is one tick
  // regardless of how many roster calls it makes (cached as one blob).
  const expectedPerSeason = 7;
  const totalEstimate = leagueKeys.length * expectedPerSeason;
  let completed = 0;

  const tick = (message) => {
    completed++;
    onProgress(message, completed, totalEstimate);
  };

  const seasons = [];
  let lastDelay = 0;
  let cacheHits = 0;
  let cacheMisses = 0;

  // A1 Patch D cache scope. leagueKeys is sorted newest-first by
  // RecapImport, so leagueKeys[0] IS the current/active season. Use its
  // league_key as the cache budget scope so all 6 data-types per all
  // seasons share one 5MB budget pool.
  const rootLeagueKey = leagueKeys[0]?.league_key;

  for (let i = 0; i < leagueKeys.length; i++) {
    const lk = leagueKeys[i];
    const seasonLabel = `${lk.season} (${i + 1}/${leagueKeys.length})`;
    const isCurrentYear = i === 0;
    // Cache-hit-aware tick — appends "(cached) ✓" to the message and
    // increments the right counter.
    const recordResult = (label, fromCache) => {
      if (fromCache) { cacheHits++; tick(`${label} (cached) ✓`); }
      else { cacheMisses++; tick(label); }
    };

    try {
      // 1. Teams
      onProgress(`Loading teams for ${seasonLabel}...`, completed, totalEstimate);
      const { data: teams, fromCache: teamsCached } = await withCache({
        leagueId: rootLeagueKey, year: lk.season, dataType: 'teams', isCurrentYear,
        fetcher: async () => {
          await delayUntil(lastDelay);
          const d = await callYahooEndpoint('/api/yahoo/teams', { token, league_key: lk.league_key }, 'teams');
          lastDelay = Date.now() + throttleMs;
          return d;
        },
      });
      recordResult(`Loaded teams for ${seasonLabel}`, teamsCached);

      // 2. Standings
      onProgress(`Loading standings for ${seasonLabel}...`, completed, totalEstimate);
      const { data: standings, fromCache: standingsCached } = await withCache({
        leagueId: rootLeagueKey, year: lk.season, dataType: 'standings', isCurrentYear,
        fetcher: async () => {
          await delayUntil(lastDelay);
          const d = await callYahooEndpoint('/api/yahoo/standings', { token, league_key: lk.league_key }, 'standings');
          lastDelay = Date.now() + throttleMs;
          return d;
        },
      });
      recordResult(`Loaded standings for ${seasonLabel}`, standingsCached);

      // 2b. League settings — needed for median-match detection
      // (settings.uses_median_score). Non-fatal: settings absent → no medians.
      // Ticks like every other phase so the progress denominator stays
      // consistent (expectedPerSeason counts this step).
      onProgress(`Loading settings for ${seasonLabel}...`, completed, totalEstimate);
      let leagueSettings = null;
      try {
        const { data: cachedSettings, fromCache: settingsCached } = await withCache({
          leagueId: rootLeagueKey, year: lk.season, dataType: 'settings', isCurrentYear,
          fetcher: async () => {
            await delayUntil(lastDelay);
            const sr = await fetch(`/api/yahoo/league_settings?token=${encodeURIComponent(token)}&league_key=${encodeURIComponent(lk.league_key)}`);
            lastDelay = Date.now() + throttleMs;
            if (sr.status === 401) { const e = new Error('Yahoo returned 401'); e.code = 'AUTH'; throw e; }
            if (!sr.ok) return null;
            const data = await sr.json();
            return data.settings || null;
          },
        });
        leagueSettings = cachedSettings;
        recordResult(`Loaded settings for ${seasonLabel}`, settingsCached);
      } catch (e) {
        if (e?.code === 'AUTH') throw e;
        // Non-AUTH settings errors are non-fatal — recap still works without
        // median detection; falls back to H2H-only records. Still tick so
        // the progress denominator stays accurate.
        tick(`Skipped settings for ${seasonLabel}`);
      }

      // 3. Scoreboards — cache the whole byWeek object as one entry per
      // season. Tradeoff: a mid-fetch failure invalidates the whole
      // season's scoreboard, but byWeek is the natural unit (~50KB)
      // and per-week granularity would 17x the entry count.
      onProgress(`Loading scoreboards for ${seasonLabel}...`, completed, totalEstimate);
      const { data: scoreboardsByWeek, fromCache: sbCached } = await withCache({
        leagueId: rootLeagueKey, year: lk.season, dataType: 'scoreboard', isCurrentYear,
        fetcher: async () => {
          const result = {};
          let consecutiveEmpty = 0;
          for (let w = 1; w <= maxWeeks; w++) {
            await delayUntil(lastDelay);
            // Fractional progress so the bar slides through the ~17-week
            // pull on cache miss (the current season + any never-cached
            // past season) instead of jumping by a whole tick at the end.
            // Sub-tick stays strictly less than 1 so the post-scoreboard
            // recordResult() lands on a clean integer position.
            const frac = (w - 1) / maxWeeks;
            onProgress(`Pulling ${seasonLabel} week ${w}...`, completed + frac, totalEstimate);
            let weekMatchups = [];
            try {
              weekMatchups = await callYahooEndpoint(
                '/api/yahoo/scoreboard',
                { token, league_key: lk.league_key, week: w },
                'matchups'
              );
            } catch (e) {
              if (e?.code === 'AUTH') throw e;
              weekMatchups = [];
            }
            lastDelay = Date.now() + throttleMs;
            if (weekMatchups.length === 0) {
              consecutiveEmpty++;
              if (consecutiveEmpty >= 2) break;
            } else {
              consecutiveEmpty = 0;
              result[w] = weekMatchups;
            }
          }
          return result;
        },
      });
      recordResult(`Loaded scoreboards for ${seasonLabel}`, sbCached);

      // Draft (task #29) — single per-season call, cached as { picks }.
      let draft = null;
      try {
        onProgress(`Loading draft for ${seasonLabel}...`, completed, totalEstimate);
        const { data: cachedDraft, fromCache: draftCached } = await withCache({
          leagueId: rootLeagueKey, year: lk.season, dataType: 'draft', isCurrentYear,
          fetcher: async () => {
            await delayUntil(lastDelay);
            const dr = await fetch(`/api/yahoo/draftresults?token=${encodeURIComponent(token)}&league_key=${encodeURIComponent(lk.league_key)}`);
            lastDelay = Date.now() + throttleMs;
            if (dr.status === 401) { const e = new Error('Yahoo returned 401'); e.code = 'AUTH'; throw e; }
            if (!dr.ok) return null;
            const data = await dr.json();
            return { picks: data.enriched_picks || [] };
          },
        });
        draft = cachedDraft;
        recordResult(`Loaded draft for ${seasonLabel}`, draftCached);
      } catch (e) {
        if (e?.code === 'AUTH') throw e;
        // Non-AUTH draft errors are non-fatal — recap still works without
        // drafts. Drafts section just hides for that season.
        tick(`Skipped draft for ${seasonLabel}`);
      }

      // Trades (task #38) — single per-season call.
      let trades = [];
      try {
        onProgress(`Loading trades for ${seasonLabel}...`, completed, totalEstimate);
        const { data: cachedTrades, fromCache: tradesCached } = await withCache({
          leagueId: rootLeagueKey, year: lk.season, dataType: 'transactions', isCurrentYear,
          fetcher: async () => {
            await delayUntil(lastDelay);
            const tr = await fetch(`/api/yahoo/transactions?token=${encodeURIComponent(token)}&league_key=${encodeURIComponent(lk.league_key)}`);
            lastDelay = Date.now() + throttleMs;
            if (tr.status === 401) { const e = new Error('Yahoo returned 401'); e.code = 'AUTH'; throw e; }
            if (!tr.ok) return [];
            const data = await tr.json();
            return Array.isArray(data.trades) ? data.trades : [];
          },
        });
        trades = cachedTrades || [];
        recordResult(`Loaded trades for ${seasonLabel}`, tradesCached);
      } catch (e) {
        if (e?.code === 'AUTH') throw e;
        tick(`Skipped trades for ${seasonLabel}`);
      }

      // Keepers (#22-keeper) — these are redraft leagues that may allow keepers
      // in ANY year, so we never gate on a settings flag. Detect per season:
      //   (a) 1 bulk /keepers?league_key call — if no kept:true anywhere, it's
      //       a redraft year, stop. Cost: 1 call.
      //   (b) if any keeper, fetch each team's roster;week=1 and union the
      //       kept player_keys. week=1 = post-draft, so keepers later dropped/
      //       traded (e.g. Courtland Sutton 2020) aren't missed. Cost: 1 + N.
      // Only runs when the season has draft picks (no picks → nothing to flag).
      // Cached as one 'keepers' blob, so a cache hit costs 0 calls.
      let keptByTeam = {};
      if (draft && Array.isArray(draft.picks) && draft.picks.length) {
        try {
          onProgress(`Loading keepers for ${seasonLabel}...`, completed, totalEstimate);
          const { data: cachedKeepers, fromCache: keepersCached } = await withCache({
            leagueId: rootLeagueKey, year: lk.season, dataType: 'keepers', isCurrentYear,
            fetcher: async () => {
              // (a) detector
              await delayUntil(lastDelay);
              const det = await fetch(`/api/yahoo/lineup?token=${encodeURIComponent(token)}&keepers=detect&league_key=${encodeURIComponent(lk.league_key)}`);
              lastDelay = Date.now() + throttleMs;
              if (det.status === 401) { const e = new Error('Yahoo returned 401'); e.code = 'AUTH'; throw e; }
              if (!det.ok) return { keptByTeam: {} };
              const detData = await det.json();
              if (!detData.anyKept) return { keptByTeam: {} }; // redraft year
              // (b) keeper year — per-team week-1 rosters
              const out = {};
              const teamKeys = (teams || []).map(t => t.team_key).filter(Boolean);
              for (const tk of teamKeys) {
                await delayUntil(lastDelay);
                const tr = await fetch(`/api/yahoo/lineup?token=${encodeURIComponent(token)}&keepers=team&team_key=${encodeURIComponent(tk)}&week=1`);
                lastDelay = Date.now() + throttleMs;
                if (tr.status === 401) { const e = new Error('Yahoo returned 401'); e.code = 'AUTH'; throw e; }
                if (!tr.ok) continue;
                const tData = await tr.json();
                if (Array.isArray(tData.keptPlayerKeys) && tData.keptPlayerKeys.length) {
                  out[tk] = tData.keptPlayerKeys;
                }
              }
              return { keptByTeam: out };
            },
          });
          keptByTeam = cachedKeepers?.keptByTeam || {};
          recordResult(`Loaded keepers for ${seasonLabel}`, keepersCached);
        } catch (e) {
          if (e?.code === 'AUTH') throw e;
          // Non-AUTH keeper errors are non-fatal — picks just render without
          // keeper badges for this season.
          tick(`Skipped keepers for ${seasonLabel}`);
        }
      } else {
        // No draft → no keeper pass, but still tick to keep the bar accurate.
        tick(`No keepers for ${seasonLabel}`);
      }

      seasons.push({
        season: String(lk.season),
        league_key: lk.league_key,
        name: lk.name,
        num_teams: lk.num_teams ?? null,
        teams,
        standings,
        scoreboardsByWeek,
        draft,
        settings: leagueSettings,
        trades,
        keptByTeam,
      });
    } catch (err) {
      // Edge #3 — auth errors mean the token expired mid-fetch. Return a
      // tokenExpired flag so the UI shows a "sign in again" button instead
      // of the generic error path.
      if (err?.code === 'AUTH') {
        return {
          seasons: seasons.length ? seasons : null,
          error: 'Yahoo authentication failed — please re-authenticate',
          tokenExpired: true,
        };
      }
      // Edge #2 — per-season error. Push a stub entry with seasonError so
      // the rest of the fetch continues and downstream knows to skip / grey
      // out this season. ONE bad season shouldn't sink the whole pull.
      seasons.push({
        season: String(lk.season),
        league_key: lk.league_key,
        name: lk.name,
        num_teams: lk.num_teams ?? null,
        teams: [],
        standings: [],
        scoreboardsByWeek: {},
        seasonError: err?.message || 'Yahoo fetch failed for this season',
      });
      onProgress(`Skipped ${lk.season} (${err?.message || 'fetch failed'}); continuing...`, completed, totalEstimate);
    }
  }

  onProgress('All seasons loaded', completed, completed);
  const errored = seasons.filter(s => s.seasonError).length;
  return { seasons, error: null, partialFailures: errored, cacheHits, cacheMisses };
}

/**
 * Teams-only prelude — fetches just /teams for each league so the alias UI
 * can show owner names before the heavy per-season fetch runs. Returns
 * minimal season payloads ({ season, league_key, teams }) compatible with
 * extractRawOwners(). Reuses the same throttling as the full fetcher so
 * Yahoo's ~1 req/sec sustained limit stays respected.
 */
export async function fetchYahooTeamsForLeagues(leagueKeys, token, options = {}) {
  if (!Array.isArray(leagueKeys) || !leagueKeys.length) {
    return { seasonsTeams: null, error: 'No league_keys supplied' };
  }
  if (!token) {
    return { seasonsTeams: null, error: 'OAuth token is required' };
  }
  const throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
  const onProgress = options.onProgress || (() => {});

  const seasonsTeams = [];
  let lastDelay = 0;

  for (let i = 0; i < leagueKeys.length; i++) {
    const lk = leagueKeys[i];
    try {
      await delayUntil(lastDelay);
      onProgress(`Loading owners for ${lk.season} (${i + 1}/${leagueKeys.length})...`, i, leagueKeys.length);
      const teams = await callYahooEndpoint('/api/yahoo/teams', { token, league_key: lk.league_key }, 'teams');
      lastDelay = Date.now() + throttleMs;
      seasonsTeams.push({ season: String(lk.season), league_key: lk.league_key, teams });
    } catch (err) {
      if (err?.code === 'AUTH') {
        return { seasonsTeams: null, error: 'Yahoo authentication failed — please re-authenticate' };
      }
      // Continue on transient errors — alias UI just shows fewer rows for
      // that one season.
      seasonsTeams.push({ season: String(lk.season), league_key: lk.league_key, teams: [] });
    }
  }
  onProgress('Owner names loaded', leagueKeys.length, leagueKeys.length);
  return { seasonsTeams, error: null };
}

// A2 lineup detail — call /api/yahoo/lineup for a single team+week. Returns
// the raw proxy response (`{ team_key, week, players: [...] }`) — caller
// runs it through yahooToLineup() to normalize. Throws with `code: 'AUTH'`
// on 401 so the pre-pull loop can abort cleanly when the OAuth token has
// expired, mirroring the existing endpoint helpers.
export async function fetchYahooLineup({ token, teamKey, week }) {
  const qs = new URLSearchParams({ token, team_key: teamKey, week: String(week) }).toString();
  const res = await fetch(`/api/yahoo/lineup?${qs}`);
  if (res.status === 401) {
    const err = new Error('Yahoo returned 401');
    err.code = 'AUTH';
    throw err;
  }
  if (!res.ok) {
    let detail = '';
    try { const b = await res.json(); detail = b.error || ''; } catch {}
    throw new Error(`/api/yahoo/lineup failed (${res.status})${detail ? ': ' + detail : ''}`);
  }
  return res.json();
}

async function callYahooEndpoint(path, params, resultField) {
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${path}?${qs}`);
  if (res.status === 401) {
    const err = new Error('Yahoo returned 401');
    err.code = 'AUTH';
    throw err;
  }
  if (!res.ok) {
    let detail = '';
    try { const b = await res.json(); detail = b.error || ''; } catch {}
    throw new Error(`${path} failed (${res.status})${detail ? ': ' + detail : ''}`);
  }
  const body = await res.json();
  return body[resultField] || [];
}

async function delayUntil(ts) {
  const wait = ts - Date.now();
  if (wait > 0) await new Promise(r => setTimeout(r, wait));
}
